import React from "react";
import { Select, SelectProps } from "antd";
import { CompanyLogo } from "../../tables/CompanyLogo";
import { Avatar } from "../../../components/visuals/Avatar";
import { Icon } from "../../visuals/styledComponents";
import styled from "styled-components";

// --- Option Label Helpers ---

const OptionLabelContainer = styled.div<{ gap?: number; paddingLeft?: number }>`
  display: flex;
  align-items: center;
  gap: ${({ gap }): string => `${gap || 8}px`};
  padding-left: ${({ paddingLeft }): string => `${paddingLeft || 0}px`};
`;

const OptionText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Helper to create option with logo (for managers/companies)
export const SelectableWithLogo = (
  id: string,
  name: string,
  image?: string
): JSX.Element => (
  <OptionLabelContainer paddingLeft={12}>
    <CompanyLogo key={id} name={name} logoUrl={image as string} size="sm" />
  </OptionLabelContainer>
);

// Helper to create option with avatar (for users)
export const SelectableWithAvatar = (
  id: string,
  name: string,
  image?: string
): JSX.Element => (
  <OptionLabelContainer gap={8}>
    <Avatar username={name} image={image} size="small" />
    <OptionText>{name}</OptionText>
  </OptionLabelContainer>
);

// Helper to create option with custom icon (SVG)
export const SelectableWithIcon = (
  icon: string,
  label: string,
  iconSize?: number
): JSX.Element => (
  <OptionLabelContainer gap={8}>
    <Icon
      src={icon}
      style={{
        width: iconSize || 20,
        height: iconSize || 20,
        flexShrink: 0,
      }}
    />
    <OptionText>{label}</OptionText>
  </OptionLabelContainer>
);

// Helper to create option with colored dot/badge
export const SelectableWithBadge = (
  label: string,
  color: string
): JSX.Element => (
  <OptionLabelContainer gap={8}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <OptionText>{label}</OptionText>
  </OptionLabelContainer>
);

// --- Types ---

export interface ModalSelectOption {
  key: string | null;
  value: string | null;
  label: React.ReactNode;
  /** Optional searchable text for complex labels */
  searchText?: string;
}

export interface ModalSelectProps extends Omit<SelectProps, "options"> {
  options: ModalSelectOption[];
  /** Minimum width in pixels */
  minWidth?: number;
  /** Right margin in pixels */
  marginRight?: number;
}

/**
 * Standardized Select component for modals and forms.
 * This is a thin wrapper around antd Select with common defaults.
 * Use this anywhere you need a Select - it's the same as antd Select
 * but with sensible defaults for dropdowns in modals.
 *
 * @example Basic usage
 * ```tsx
 * <ModalSelect
 *   options={[{ key: '1', value: '1', label: 'Option 1' }]}
 *   placeholder="Select option"
 *   onChange={(value) => console.log(value)}
 * />
 * ```
 *
 * @example With logos (for companies/managers)
 * ```tsx
 * const options = managers.map(m => ({
 *   key: m.id,
 *   value: m.id,
 *   label: SelectableWithLogo(m.id, m.name, m.logo),
 * }));
 * ```
 */
export const ModalSelect: React.FC<ModalSelectProps> = ({
  options,
  allowClear = true,
  showSearch = true,
  optionLabelProp = "label",
  minWidth,
  marginRight,
  style,
  ...rest
}) => {
  return (
    <Select
      style={{ minWidth, marginRight, ...style }}
      options={options}
      allowClear={allowClear}
      showSearch={showSearch}
      optionLabelProp={optionLabelProp}
      dropdownStyle={{ zIndex: 2147483647 }}
      getPopupContainer={(): HTMLElement => document.body}
      filterOption={(input, option): boolean => {
        // Use searchText if provided (for complex labels)
        if (option?.searchText) {
          return String(option.searchText)
            .toLowerCase()
            .includes(input.toLowerCase());
        }
        // Fall back to label if it's a string
        const label = option?.label;
        if (typeof label === "string") {
          return label.toLowerCase().includes(input.toLowerCase());
        }
        // For complex labels without searchText, search by value
        return String(option?.value || "")
          .toLowerCase()
          .includes(input.toLowerCase());
      }}
      {...rest}
    />
  );
};

export default ModalSelect;
